import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

/**
 * ErrorBoundary com UI fallback acessível.
 * - role="alert" para anunciar a falha a leitores de tela.
 * - Texto literal, sem ironia (princípio UX 1).
 * - Reload da página é sempre uma rota de saída segura.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // TODO M3.x: enviar para Cloud Logging via callable de telemetria opt-in.
    // Por enquanto, console é aceitável (não exporta dados clínicos).
    if (typeof console !== 'undefined') {
      console.error('[Lume] ErrorBoundary capturou exceção:', error, info.componentStack);
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    const error = this.state.error ?? new Error('Erro desconhecido');
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div
        role="alert"
        className="mx-auto flex min-h-screen max-w-xl flex-col justify-center gap-4 p-6"
      >
        <h1 className="text-2xl font-bold text-fg-DEFAULT">Algo travou nesta tela.</h1>
        <p className="text-fg-muted">
          Sua sessão segue ativa. Volte e tente de novo. Se o problema continuar, peça apoio à
          equipe TEACare.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="self-start rounded-lg bg-brand-800 px-5 py-3 font-medium text-white"
        >
          Recarregar a página
        </button>
      </div>
    );
  }
}
